/**
 * §5.11 Ф4 — конфиг бренда витрины, выбираемый по VITE_BRAND (дефолт vkusnodomoy).
 *
 * Один и тот же код обслуживает разные демо-витрины без форка: ВкусноДомой (ритейл,
 * Ф5) и ВкусноКафе (HORECA-кафе «как chaihona», Ф4). Ф5 переиспользует этот механизм.
 * Бренд = имя/слоган/эмодзи-лого/адрес + переопределения дизайн-токенов --vd-*.
 */

export interface Brand {
  id: string;
  /** Полное имя (хедер, экран входа). */
  name: string;
  /** Короткое имя (PWA, вкладка). */
  shortName: string;
  /** Слоган/подзаголовок. */
  tagline: string;
  /** Эмодзи-лого (экран входа). */
  logoEmoji: string;
  /** Адрес точки (хедер). */
  address: string;
  /** Подпись рядом с адресом в хедере (ETA доставки / режим работы кафе). */
  addressEta: string;
  /** Плейсхолдер строки поиска («продукты» для ритейла / «блюда» для кафе). */
  searchPlaceholder: string;
  /** Имя во вкладке браузера (суффикс заголовка). */
  tabName: string;
  /** Цвет вкладки браузера (meta theme-color). */
  themeColor: string;
  /** Переопределения дизайн-токенов --vd-* ({} = дефолтная палитра style.css). */
  colors: Record<string, string>;
}

const vkusnodomoy: Brand = {
  id: "vkusnodomoy",
  name: "ВкусноДомой",
  shortName: "ВкусноДомой",
  tagline: "Свежие продукты к двери",
  logoEmoji: "🥦",
  address: "Тверская, 1",
  addressEta: "за 15 мин",
  searchPlaceholder: "Найти продукты",
  tabName: "ВкусноДомой",
  themeColor: "#2E9E5B",
  colors: {}, // дефолтная зелёная палитра из style.css
};

const vkusnokafe: Brand = {
  id: "vkusnokafe",
  name: "ВкусноКафе",
  shortName: "ВкусноКафе",
  tagline: "Чайхана — тепло и уют",
  logoEmoji: "🍵",
  address: "Пятницкая, 12",
  addressEta: "ежедневно",
  searchPlaceholder: "Найти блюда",
  tabName: "ВкусноКафе",
  themeColor: "#C1512B",
  // Тёплая палитра чайханы (терракота + шафран) вместо свежего зелёного ритейла.
  colors: {
    "--vd-brand": "#C1512B",
    "--vd-brand-press": "#A5431F",
    "--vd-brand-ink": "#8A3A1E",
    "--vd-brand-tint": "#F6EBE2",
    "--vd-page": "#FAF6F1",
    "--vd-tint": "#F3ECE4",
    "--vd-star": "#E0A11E",
    "--vd-new-bg": "#EAF0E7", "--vd-new-ink": "#5A7A2E",
  },
};

const BRANDS: Record<string, Brand> = { vkusnodomoy, vkusnokafe };

/**
 * Бренд определяется в РАНТАЙМЕ — один образ витрины обслуживает несколько демо
 * (ВкусноКафе / ВкусноДомой) без пересборки. Приоритет:
 *   1) window.__STOREFRONT_BRAND__ — явная инъекция per-инстанс (index.html / nginx);
 *   2) по домену (vk.* / *kafe* / chaihona → кафе; vd.* / *vkusnodomoy* → ритейл);
 *   3) build-time VITE_BRAND (локальная разработка), иначе дефолт vkusnodomoy.
 */
function resolveBrandId(): string {
  if (typeof window !== "undefined") {
    const inj = (window as unknown as { __STOREFRONT_BRAND__?: string }).__STOREFRONT_BRAND__;
    if (inj && BRANDS[inj]) return inj;
    const h = window.location.hostname.toLowerCase();
    if (/^vk\.|kafe|chaihona/.test(h)) return "vkusnokafe";
    if (/^vd\.|vkusnodomoy|domoy/.test(h)) return "vkusnodomoy";
  }
  const vite = (typeof import.meta !== "undefined" && (import.meta.env?.VITE_BRAND as string | undefined)) || "";
  return BRANDS[vite] ? vite : "vkusnodomoy";
}

const BRAND_ID = resolveBrandId();

/** Активный бренд витрины (рантайм: инъекция → домен → VITE_BRAND). */
export const brand: Brand = BRANDS[BRAND_ID] ?? vkusnodomoy;

/** Применить дизайн-токены бренда к :root. Вызывать в main.ts ДО mount. */
export function applyBrandTheme(target: HTMLElement = document.documentElement): void {
  for (const [k, v] of Object.entries(brand.colors)) target.style.setProperty(k, v);
}
